import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, KeyRound, CheckCircle } from 'lucide-react';
import { affiliateAPI } from '../services/affiliateApi';

const AffiliateSetupPasswordPage = () => {
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const resetToken = searchParams.get('reset');
  const isReset = !!resetToken;

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const t = (en, es, ru) => currentLanguage === 'es' ? es : currentLanguage === 'ru' ? ru : en;

  useEffect(() => {
    if (!token && !resetToken) {
      setError(t('Invalid link.', 'Enlace inválido.', 'Неверная ссылка.'));
    }
  }, []); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError(t('Password must be at least 8 characters.',
                 'La contraseña debe tener al menos 8 caracteres.',
                 'Пароль должен быть не менее 8 символов.'));
      return;
    }
    if (password !== confirm) {
      setError(t('Passwords do not match.', 'Las contraseñas no coinciden.', 'Пароли не совпадают.'));
      return;
    }
    setLoading(true);
    try {
      if (isReset) {
        await affiliateAPI.resetPassword(resetToken, password);
        setDone(true);
        setTimeout(() => navigate('/affiliate/login'), 1500);
      } else {
        await affiliateAPI.setupPassword(token, password);
        setDone(true);
        setTimeout(() => navigate('/affiliate/dashboard'), 1200);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-capsule-bg">
      <SEOHead page="affiliate" />
      <Header />
      <main className="container mx-auto px-4 py-16 md:py-24" data-testid="affiliate-setup-page">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-capsule-teal to-capsule-aqua mb-4 shadow-teal-glow">
              <KeyRound className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              {isReset ? t('Reset Password', 'Restablecer Contraseña', 'Сброс пароля')
                       : t('Set Your Password', 'Establecer Contraseña', 'Установить пароль')}
            </h1>
            <p className="text-gray-600">
              {t('Choose a strong password (8+ characters)',
                 'Elija una contraseña segura (8+ caracteres)',
                 'Выберите надёжный пароль (8+ символов)')}
            </p>
          </div>

          <Card className="capsule-card">
            <CardHeader>
              <CardTitle>{t('New Password', 'Nueva Contraseña', 'Новый пароль')}</CardTitle>
            </CardHeader>
            <CardContent>
              {done ? (
                <div className="text-center py-6">
                  <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <p className="text-gray-700">
                    {t('Password set! Redirecting...', 'Contraseña establecida. Redirigiendo...', 'Пароль установлен. Перенаправление...')}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="pw">{t('Password', 'Contraseña', 'Пароль')}</Label>
                    <Input id="pw" type="password" required value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="setup-password-input" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="cpw">{t('Confirm Password', 'Confirmar Contraseña', 'Подтвердите пароль')}</Label>
                    <Input id="cpw" type="password" required value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      data-testid="setup-confirm-input" className="mt-1" />
                  </div>
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-md" data-testid="setup-error">
                      {error}
                    </div>
                  )}
                  <Button type="submit" disabled={loading || (!token && !resetToken)}
                    className="w-full bg-gradient-to-r from-capsule-teal to-capsule-aqua text-white py-2.5 rounded-2xl"
                    data-testid="setup-submit-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t('Continue', 'Continuar', 'Продолжить')}
                  </Button>
                  <div className="text-center text-sm">
                    <Link to="/affiliate/login" className="text-capsule-teal hover:underline">
                      {t('Back to login', 'Volver al inicio', 'Назад')}
                    </Link>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AffiliateSetupPasswordPage;
